import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { type CsatRating } from '../../services/feedback/jsm-feedback';

export interface UseCsatSurveyStateParams {
	conversationId: string | undefined;
	agentMessageId: string;
	isEligibleToShow: boolean;
	/**
	 * Sticky veto from the parent. When `true`, hides CSAT regardless of the
	 * `csatFlowUsedThisTurn` snapshot — used to suppress CSAT for any message
	 * that ever had a pending action card.
	 */
	hadPendingActionAtAnyPoint?: boolean;
	shouldSuppressCsatForAgent?: boolean;
	handleCsatFeedback: (rating: CsatRating) => boolean;
	onCsatInteracted: () => void;
	onCsatDisplayed: () => void;
}

/**
 * `compact` = stacked radio-row layout;
 * `full` = horizontal scale layout
 */
export type CsatSurveyLayout = 'compact' | 'full';

export interface UseCsatSurveyStateResult {
	csatRating: CsatRating | null;
	handleSelectRating: (rating: CsatRating | null) => void;
	handleCsatSubmit: (rating: CsatRating) => void;
	handleCsatDismiss: () => void;
	isCsatSurveyVisible: boolean;
	csatFlowUsedThisTurn: boolean;
	recordCsatSurveyShown: () => void;
	recordResolvedLayout: (layout: CsatSurveyLayout) => void;
}

/**
 * Owns all CSAT survey UI state for a single agent message: rating selection,
 * dismissed state, the per-turn "flow active" latch, and the GASv3 analytics events.
 *
 */
export const useCsatSurveyState = ({
	conversationId,
	agentMessageId,
	isEligibleToShow,
	hadPendingActionAtAnyPoint = false,
	shouldSuppressCsatForAgent = false,
	handleCsatFeedback,
	onCsatInteracted,
	onCsatDisplayed,
}: UseCsatSurveyStateParams): UseCsatSurveyStateResult => {
	const { sendAnalyticsEvent } = useAnalytics();

	const [csatRating, setCsatRating] = useState<CsatRating | null>(null);
	const [isCsatSurveyDismissed, setIsCsatSurveyDismissed] = useState(false);

	const resolvedLayoutRef = useRef<CsatSurveyLayout | null>(null);
	const recordResolvedLayout = useCallback((layout: CsatSurveyLayout) => {
		if (resolvedLayoutRef.current !== null) {
			return;
		}
		resolvedLayoutRef.current = layout;
	}, []);

	// Fire a single "clicked" event for the first rating option a user picks in
	// this CSAT survey instance. Switching between rating options (or
	// programmatic resets to `null`) must NOT re-fire the event so we get an
	// accurate count of users who engaged with the survey but never submitted.
	const csatOptionClickedFiredRef = useRef(false);
	const handleSelectRating = useCallback(
		(rating: CsatRating | null) => {
			if (rating !== null && !csatOptionClickedFiredRef.current) {
				csatOptionClickedFiredRef.current = true;
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'clicked',
					actionSubject: 'csatSurveyOption',
					attributes: {
						csatRating: rating,
						conversationId,
						messageId: agentMessageId,
						layout: resolvedLayoutRef.current,
					},
				});
			}
			setCsatRating(rating);
		},
		[sendAnalyticsEvent, conversationId, agentMessageId],
	);

	// Sticky per-message latch: once eligibility is true at any point during this
	// message lifecycle, keep the flow active for the rest of the turn.
	// Commit-safe reducer transition (false -> true only), then OR with current
	// eligibility so the first eligible render can show immediately.
	const [csatFlowLatchedThisTurn, latchCsatFlowThisTurn] = useReducer(
		(previous: boolean, next: boolean) => previous || next,
		isEligibleToShow,
	);
	useEffect(() => {
		if (isEligibleToShow) {
			latchCsatFlowThisTurn(true);
		}
	}, [isEligibleToShow]);
	const csatFlowUsedThisTurn = csatFlowLatchedThisTurn || isEligibleToShow;

	const isCsatSurveyVisible =
		csatFlowUsedThisTurn &&
		!hadPendingActionAtAnyPoint &&
		!shouldSuppressCsatForAgent &&
		!isCsatSurveyDismissed;

	const csatShownFiredRef = useRef(false);
	const recordCsatSurveyShown = useCallback(() => {
		if (csatShownFiredRef.current) {
			return;
		}
		csatShownFiredRef.current = true;
		// Start the 7-day soft cooldown so the survey doesn't re-appear on every response when the user ignores it.
		onCsatDisplayed();
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'shown',
			actionSubject: 'csatSurvey',
			attributes: {
				conversationId,
				messageId: agentMessageId,
				layout: resolvedLayoutRef.current,
			},
		});
	}, [sendAnalyticsEvent, conversationId, agentMessageId, onCsatDisplayed]);

	const handleCsatDismiss = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'dismissed',
			actionSubject: 'csatSurvey',
			attributes: {
				conversationId,
				messageId: agentMessageId,
				layout: resolvedLayoutRef.current,
			},
		});
		setCsatRating(null);
		setIsCsatSurveyDismissed(true);
		onCsatInteracted();
	}, [sendAnalyticsEvent, conversationId, agentMessageId, onCsatInteracted]);

	const handleCsatSubmit = useCallback(
		(rating: CsatRating) => {
			const isLike = handleCsatFeedback(rating);
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'submitted',
				actionSubject: 'csatSurvey',
				attributes: {
					csatRating: rating,
					isLike,
					conversationId,
					messageId: agentMessageId,
					layout: resolvedLayoutRef.current,
				},
			});
			setCsatRating(null);
			// Close the CSAT card after submit so it doesn't sit on screen
			// behind the detailed-feedback survey that `handleCsatFeedback` opens.
			setIsCsatSurveyDismissed(true);
			onCsatInteracted();
		},
		[sendAnalyticsEvent, conversationId, agentMessageId, handleCsatFeedback, onCsatInteracted],
	);

	return {
		csatRating,
		handleSelectRating,
		handleCsatSubmit,
		handleCsatDismiss,
		isCsatSurveyVisible,
		csatFlowUsedThisTurn,
		recordCsatSurveyShown,
		recordResolvedLayout,
	};
};
